import { getApiBaseUrl } from 'shared/helper/request';

/**
 * Checkout links in Rubriq emails are addressed to this site, so that what lands in the
 * researcher's inbox carries the domain they signed up on, but megatron is what knows whose
 * link it is: it asks Paddle for a transaction and redirects on to `/rubriq/pay?_ptxn=...`.
 *
 * Leaving before the app mounts keeps the loading screen off a page whose only job is to
 * redirect. {@see checkoutLinkTokenFromPath} answers null for everything that is not a
 * checkout link, so no other route is touched.
 */
var LINK_SEGMENT = '/rubriq/pay-link/';

// The route, optionally language-prefixed, ending in one of megatron's tokens - 32 random
// bytes, hex encoded.
var LINK_PATH = /^\/(?:(?:br|cn|en|es|jp)\/)?rubriq\/pay-link\/([0-9a-f]{64})\/?$/i;

/** Why megatron sent someone to the pay page without a transaction to check out. */
export var CHECKOUT_LINK_OUTCOMES = ['expired', 'invalid', 'unavailable', 'already_subscribed', 'failed'];

/**
 * The plan a link can ask for (`?plan=translation&interval=monthly`), passed on to megatron as
 * it is. Megatron reads a missing plan as editing and a missing interval as yearly, and a link
 * naming neither keeps its default.
 */
export var CHECKOUT_LINK_PLANS = ['editing', 'translation'];
export var CHECKOUT_LINK_INTERVALS = ['yearly', 'monthly'];
export var checkoutLinkTokenFromPath = function checkoutLinkTokenFromPath() {
  var pathname = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : window.location.pathname;
  // Every other page leaves here, before the pattern is evaluated.
  if (pathname.indexOf(LINK_SEGMENT) === -1) {
    return null;
  }
  var match = pathname.match(LINK_PATH);
  return match ? match[1] : null;
};
export var checkoutLinkUrlForToken = function checkoutLinkUrlForToken(token) {
  var search = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : '';
  var params = new URLSearchParams(search);
  var forwarded = new URLSearchParams();
  var plan = params.get('plan');
  if (CHECKOUT_LINK_PLANS.includes(plan)) {
    forwarded.set('plan', plan);
  }
  var interval = params.get('interval');
  if (CHECKOUT_LINK_INTERVALS.includes(interval)) {
    forwarded.set('interval', interval);
  }
  var query = forwarded.toString() ? "?".concat(forwarded.toString()) : '';
  return "".concat(getApiBaseUrl(), "/api/curie-checkout-link/").concat(encodeURIComponent(token)).concat(query);
};
export var redirectToCheckoutLink = function redirectToCheckoutLink(token) {
  var search = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : window.location.search;
  window.location.replace(checkoutLinkUrlForToken(token, search));
};

/**
 * What the pay page was asked to do, read from its query string: open the checkout for a
 * Paddle transaction (`_ptxn`), or explain why there is none (`checkoutLink`). Megatron adds
 * the plan the checkout subscribes to (`plan`, `interval`); there is none when it only adds a
 * payment method to a running trial.
 */
export var checkoutRequestFromSearch = function checkoutRequestFromSearch() {
  var search = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : window.location.search;
  var params = new URLSearchParams(search);
  var outcome = params.get('checkoutLink');
  var plan = params.get('plan');
  var interval = params.get('interval');
  return {
    transactionId: params.get('_ptxn') || null,
    outcome: CHECKOUT_LINK_OUTCOMES.includes(outcome) ? outcome : null,
    plan: CHECKOUT_LINK_PLANS.includes(plan) ? plan : null,
    interval: CHECKOUT_LINK_INTERVALS.includes(interval) ? interval : null
  };
};
export default checkoutLinkTokenFromPath;